import { z } from "zod";

export const OfferYachtForm = z.object({
  paymentSteps: z.array(
    z.object({
      amount: z.number(),
      operatorAmount: z.number(),
      days: z.number().int().nonnegative(),
      type: z.enum(["on confirmation", "after confirmation", "before booking date"]),
      originalPercentage: z.number().optional().nullable(),
      isNewStep: z.boolean().optional(),
      scheduleStep: z.number(),
      isNewOffer: z.boolean().optional(),
    }),
  ),
  cardPayments: z.boolean(),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  discountCharterPrice: z.number().optional(),
  securityDeposit: z.number().optional(),
  yachtOnHold: z.boolean(),
  ignoreAvailability: z.boolean().optional().default(false),
  brokerNote: z.string().nullable().optional(),
  holdExpiresAt: z.string().nullable().optional(),
  startLocation: z.object({
    id: z.string().min(1),
    names: z.string().min(1),
  }),
  endLocation: z.object({
    id: z.string().min(1),
    names: z.string().min(1),
  }),
  startTime: z.string(),
  endTime: z.string(),
  amenitiesDetails: z.array(
    z.object({
      id: z.string().optional(),
      paidLocally: z.boolean(),
      price: z.number(),
      obligatory: z.boolean(),
      text: z.string().optional(),
      description: z.string().optional(),
      number: z.number().optional().nullable(),
      formula: z.string().optional(),
      provider: z.enum(["NS", "CYA", "BM", "Helm", "Ankor"]).optional(),
      checked: z.boolean(),
      currency: z.string().optional(),
      currencyId: z.string().optional(),
      hidden: z.boolean().optional(),
      freeOfCharge: z.boolean().optional(),
      isCustomAmenity: z.boolean().optional(),
      included: z.boolean().optional(),
    }),
  ),
  customAmenities: z.array(
    z.object({
      id: z.string().optional(),
      dedupeKey: z.string().optional(),
      text: z.string(),
      comment: z.string().optional(),
      included: z.boolean().optional(),
      obligatory: z.boolean(),
      paid: z.boolean().optional(),
      paidLocally: z.boolean(),
      price: z.number(),
      finalBookingPrice: z.number(),
      number: z.number(),
      isPercentage: z.boolean().optional(),
      manuallyAssigned: z.boolean().optional(),
      per: z.string().optional(),
      percentageType: z.string().optional(),
      provider: z.enum(["NS", "CYA", "BM", "Helm", "Ankor"]).optional(),
      currencyId: z.string(),
      currencyCode: z.string().optional(),
      yachtId: z.string().optional(),
      isCustomAmenity: z.boolean(),
      checked: z.boolean(),
      hidden: z.boolean().optional(),
      freeOfCharge: z.boolean().optional(),
      yachtIndex: z.number().optional(),
    }),
  ),
  pricing: z.object({
    discountPrice: z.number(),
    discountPercentage: z.number(),
    depositPrice: z.number(),
    totalPrice: z.number(),
    originalPrice: z.number(),
    clientPrice: z.number(),
    clientPriceApi: z.number(),
    helmCustomDiscountPrice: z.number(),
    operatorPayablePrice: z.number(),
    operatorTotalPrice: z.number(),
    extrasPriceInOperatorCurrency: z.number(),
    commissionPostHelmDiscountPrice: z.number(),
    includedFree: z.array(z.object({ text: z.string() })).or(z.tuple([])),
    pickupObligatory: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
    advanceObligatory: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
    advanceExtras: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
    pickupExtras: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
    yachtCurrencyCode: z.string().optional().nullable(),
    vatPrice: z.number().optional(),
    vatPercentage: z.string().optional(),
    apaPrice: z.number().optional(),
    apaPercentage: z.string().optional(),
    deliveryFeesPrice: z.number().optional(),
    charterTerms: z.enum(["inclusive", "exclusive"]).optional(),
  }),
  // set when the yacht was added via quick-add and its live price/availability
  // was fetched in the background; surfaced as a badge on the Configure step.
  availabilityChecked: z.boolean().optional(),
  checkedAt: z.string().optional().nullable(),
  id: z.number().optional(),
});

export type OfferYachtForm = z.infer<typeof OfferYachtForm>;

export const emptyOfferYacht: OfferYachtForm = {
  paymentSteps: [],
  cardPayments: false,
  yachtOnHold: false,
  startLocation: {
    id: "",
    names: "",
  },
  endLocation: {
    id: "",
    names: "",
  },
  discountCharterPrice: 0,
  securityDeposit: 0,
  startDate: "",
  endDate: "",
  startTime: "12:00",
  endTime: "12:00",
  amenitiesDetails: [],
  customAmenities: [],
  ignoreAvailability: false,
  availabilityChecked: false,
  checkedAt: null,
  pricing: {
    discountPrice: 0,
    depositPrice: 0,
    discountPercentage: 0,
    totalPrice: 0,
    originalPrice: 0,
    clientPrice: 0,
    clientPriceApi: 0,
    helmCustomDiscountPrice: 0,
    operatorPayablePrice: 0,
    commissionPostHelmDiscountPrice: 0,
    operatorTotalPrice: 0,
    extrasPriceInOperatorCurrency: 0,
    advanceObligatory: [],
    pickupObligatory: [],
    advanceExtras: [],
    pickupExtras: [],
    includedFree: [],
  },
};
